/**
 * Error Handling Utilities
 *
 * Provides type-safe error handling functions for API responses
 * and general error scenarios.
 */

import type { AxiosError } from 'axios';
import type { ApiError } from '@/types';

/**
 * API Error Response structure from backend
 */
interface ApiErrorResponse {
  success: false;
  error: ApiError | string;
}

/**
 * Extract error message from various error types
 *
 * @example
 * try {
 *   await api.post('/endpoint', data);
 * } catch (err) {
 *   setError(getErrorMessage(err));
 * }
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  // Handle Axios errors with API response
  if (isAxiosError(error)) {
    const response = error.response?.data as ApiErrorResponse | undefined;

    if (response?.error) {
      if (typeof response.error === 'string') {
        return response.error;
      }
      return response.error.message;
    }

    // Network error
    if (error.code === 'ERR_NETWORK') {
      return 'Unable to connect to server. Please check your internet connection.';
    }

    // Timeout error
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }

    // HTTP status-based fallbacks
    const status = error.response?.status;
    if (status === 401) return 'Invalid credentials. Please try again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 404) return 'The requested resource was not found.';
    if (status === 422) return 'Validation failed. Please check your input.';
    if (status === 429) return 'Too many requests. Please try again later.';
    if (status && status >= 500) return 'Server error. Please try again later.';
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * Type guard for Axios errors
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extract validation errors from API response
 *
 * @returns Object with field names as keys and error messages as values
 */
export function getValidationErrors(error: unknown): Record<string, string> | null {
  if (!isAxiosError(error)) return null;

  const response = error.response?.data as ApiErrorResponse | undefined;
  const apiError = response?.error;

  if (typeof apiError === 'object' && apiError?.details) {
    const errors: Record<string, string> = {};
    for (const [field, messages] of Object.entries(apiError.details)) {
      errors[field] = Array.isArray(messages) ? messages[0] : String(messages);
    }
    return errors;
  }

  return null;
}

/**
 * Check if error is a specific HTTP status code
 */
export function isHttpError(error: unknown, statusCode: number): boolean {
  return isAxiosError(error) && error.response?.status === statusCode;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  return isHttpError(error, 401);
}

/**
 * Check if error is a forbidden error (403)
 */
export function isForbiddenError(error: unknown): boolean {
  return isHttpError(error, 403);
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return isHttpError(error, 404);
}

/**
 * Check if error is a validation error (422)
 */
export function isValidationError(error: unknown): boolean {
  return isHttpError(error, 422);
}
